import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, Platform } from 'react-native'
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthRhythm } from '../../lib/hooks/use-auth-rhythm'
import { AuthGradientButton } from '../../components/auth/AuthGradientButton'
import { PhoneOtpDeliveryRecovery } from '../../components/auth/PhoneOtpDeliveryRecovery'
import { impact, ImpactStyle, notification, NotificationType } from '@/lib/utils/haptics'
import Animated, { FadeInDown, FadeInUp, useReducedMotion } from 'react-native-reanimated'
import { AuthErrorBanner } from '../../components/auth/AuthErrorBanner'
import { Pressable } from '../../components/ui/Pressable'
import { PhoneInput } from '../../components/ui/PhoneInput'
import { BackButton } from '../../components/ui/BackButton'
import { AuthScaffold } from '../../components/auth/AuthScaffold'
import { authKeys, useAuth } from '../../lib/hooks/use-auth'
import { useAuthStore } from '../../lib/stores/auth.store'
import { authService } from '../../lib/services/auth.service'
import { productTelemetryService } from '../../lib/services/product-telemetry.service'
import {
  classifyOtpIneligibility,
  canDeliverOtp,
  otpIneligibilityMessageKey,
} from '../../lib/utils/otp-delivery'
import {
  classifyPhoneOtpDeliveryError,
  isPhoneAccountUsesEmailError,
  phoneOtpRetryAfterSeconds,
  type PhoneOtpDeliveryReason,
} from '../../lib/errors/service-error'
import { getContactVerificationRequirement } from '../../lib/utils/account-contact'
import { takeStagedPhoneForLogin } from '../../lib/auth/phone-handoff'
import { openPhoneOtpSupport } from '../../lib/auth/open-phone-otp-support'
import { captureError, errorMessage } from '../../lib/utils/observability'
import { toast } from '../../lib/toast'
import { env } from '../../lib/env'
import { ConsentForm } from '../../components/auth/ConsentForm'
import type { ConsentValues } from '../../lib/types/auth.types'
import { FONTS, SPACING } from '../../lib/brand'
import { useSemanticColors, DURATION, INTERACTION, STAGGER } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'

const isWeb = Platform.OS === 'web'
type PhoneAuthMode = 'login' | 'signup'

export default function PhoneLoginScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const pathname = usePathname()
  const { mode: rawMode } = useLocalSearchParams<{ mode?: string }>()
  const mode: PhoneAuthMode =
    rawMode === 'signup' || pathname.includes('phone-signup') ? 'signup' : 'login'
  const isSignup = mode === 'signup'
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop, width } = useBreakpoint()
  const { sendOtp, sendOtpPending, signInWithGoogle, googlePending } = useAuth()
  const queryClient = useQueryClient()
  // The phone is handed to the OTP screen through the in-memory auth store,
  // never through router params: expo-router serialises params into the web
  // query string, which would leak the number into browser history, hosting
  // access logs and Sentry's browser-tracing URL capture.
  const setPendingAuthPhone = useAuthStore((s) => s.setPendingAuthPhone)
  const markCredentialUnlock = useAuthStore((s) => s.markCredentialUnlock)
  const setCompletingSignup = useAuthStore((s) => s.setCompletingSignup)
  const isSendingRef = useRef(false)
  const reducedMotion = useReducedMotion()

  const phoneSendErrorMessage = (err: unknown) => {
    const reason = classifyPhoneOtpDeliveryError(err)
    if (isPhoneAccountUsesEmailError(err)) return t('auth.errors.phone_account_uses_email')
    if (reason === 'service_rate_limited') return t('auth.verify_phone_sms_busy_body')
    if (reason === 'account_rate_limited') return t('auth.verify_phone_sms_quota_body')
    if (reason === 'provider_rejected') return t('auth.verify_phone_sms_provider_body')
    return errorMessage(err, t('auth.errors.otp_send_failed'))
  }

  // One breakpoint for the whole auth flow. This screen used to read
  // `useWindowDimensions()` and cut over at 900px while login.tsx cut over at
  // the canonical desktop breakpoint, so the headline changed size mid-flow.
  const isWide = isDesktop
  // Same cut-over AuthScaffold uses to decide between the centered desktop
  // column and the full-height mobile scroll body. Only the mobile branch gets
  // the flexible footer spacer below: on the split layout the column is already
  // vertically centered, so a spacer would top-align the form instead.
  const isSplit = width >= 900

  // PhoneInput emits clean E.164 (or '' until 8 digits entered). Prefill from
  // phone-in-use recovery handoff (survives SIGNED_OUT store wipe).
  // ONE take, two states. `takeStagedPhoneForLogin` CONSUMES the handoff, so
  // calling it from a second useState initializer hands back '' and the
  // recovered number silently loses its validity seed.
  const stagedPhoneRef = useRef<string | null>(null)
  if (stagedPhoneRef.current === null) stagedPhoneRef.current = takeStagedPhoneForLogin() ?? ''
  const stagedPhone = stagedPhoneRef.current
  const [phone, setPhone] = useState(stagedPhone)
  /**
   * THE BARRIER. Starts false and only libphonenumber can turn it true.
   *
   * Before this, the CTA was always tappable and every rule ran AFTER the tap:
   * a landline, a country we do not serve, a number three digits short — each
   * one made the round trip, and the provider charges for the attempt, not for
   * the delivery. The whole point of a numbering plan on the device is that the
   * request for a number that cannot exist never leaves it.
   *
   * `phoneCandidate` is prefix+digits as typed, kept so a disabled button can
   * still say why. Silence and a grey button is not a smaller bug than a wasted
   * credit — it is just a cheaper one for us and a worse one for the person.
   */
  const [isValid, setIsValid] = useState(false)
  const [phoneCandidate, setPhoneCandidate] = useState(stagedPhone)
  // No hay selector de canal: el código viaja por WhatsApp y solo por WhatsApp.
  // Antes había dos, y el segundo nunca se usó — la bandera que lo mostraba
  // llevaba apagada desde que se escribió, y encenderla habría activado una ruta
  // del proveedor anterior que tampoco estaba contratada. Un control que ofrece
  // una opción que no existe es peor que no ofrecer ninguna.
  // Explicit legal acceptance on phone-signup (Apple/App Store expectation).
  // completeSignup only runs after OTP when the person has checked this.
  const [consent, setConsent] = useState<ConsentValues>({ acceptTerms: false })
  const [consentError, setConsentError] = useState<string | undefined>()
  // Two error surfaces, two states. `phoneError` is the field-level message
  // PhoneInput renders under the number; `error` is the send failure the
  // banner announces above the CTA.
  const [phoneError, setPhoneError] = useState('')
  const [error, setError] = useState('')
  const [deliveryIssue, setDeliveryIssue] = useState<{
    reason: PhoneOtpDeliveryReason
  } | null>(null)
  const [retrySeconds, setRetrySeconds] = useState(0)
  const recordPhoneRequestEvent = (
    eventName: string,
    elementId: string,
    elementLabel: string,
    extra?: Record<string, unknown>,
  ) => {
    productTelemetryService.recordEvent('form_submit', eventName, {
      routePath: isSignup ? '/phone-signup' : '/phone-login',
      screenName: isSignup ? 'phone-signup' : 'phone-login',
      elementId,
      elementType: 'auth_step',
      metadata: {
        ...extra,
        elementLabel,
        mode,
        channel: 'whatsapp',
        phonePresent: Boolean(phone),
      },
    })
  }

  /**
   * The onChange half of the state machine.
   *
   * Runs on every keystroke, entirely on-device: concatenate the picker's dial
   * code with the typed digits, hand the result to libphonenumber, and let the
   * answer own the button. Nothing here is async and nothing here can reach the
   * network — that is the property this whole gate exists to have.
   */
  const handlePhoneValidityChange = useCallback((valid: boolean, candidate: string) => {
    setIsValid(valid)
    setPhoneCandidate(candidate)
  }, [])

  /**
   * What to say while the button is inert.
   *
   * Deliberately NOT rendered as an error: a half-typed number is not a
   * mistake, and painting the field red on the fourth digit of eight teaches
   * people to distrust the field. `helperText` is the calm channel; `error`
   * stays for what actually failed after a tap.
   */
  const phoneHint = useMemo(() => {
    if (isValid || phoneError) return null
    const reason = classifyOtpIneligibility(phoneCandidate)
    // Still typing, or nothing typed. Say nothing.
    if (!reason || reason === 'empty' || reason === 'not_a_number') return null
    const key = otpIneligibilityMessageKey(reason)
    return key ? t(key) : null
  }, [isValid, phoneError, phoneCandidate, t])

  const sp = useAuthRhythm()

  const handleSend = async () => {
    if (isSendingRef.current || sendOtpPending) return
    impact(ImpactStyle.Light)

    // Recovery: a prior OTP may already have created a live session while the
    // UI bounced to welcome (session-cache race). Don't burn another SMS —
    // seed React Query (session alone is not enough for isAuthenticated) and
    // enter via the same handoff as phone-verify.
    try {
      const existing = await authService.getSession()
      if (existing?.user) {
        queryClient.setQueryData(authKeys.session(), existing)
        setCompletingSignup(true)
        markCredentialUnlock()
        recordPhoneRequestEvent(
          'auth.phone_otp_request_already_authenticated',
          'phone-login.submit-button',
          'Sesión ya activa',
        )
        const requirement = getContactVerificationRequirement(existing.user, null)
        const route =
          requirement === 'email'
            ? '/(auth)/verify-email'
            : requirement === 'phone'
              ? '/(auth)/verify-phone'
              : '/(tabs)/explore'
        router.replace(route as never)
        return
      }
    } catch {
      // Fall through to normal OTP send.
    }

    // Second lock on the same door. The button is already inert for anything
    // this refuses, so reaching here means the state machine and the number
    // disagreed — a stale `isValid` after a country switch, a synthetic tap in
    // a test. Either way the rule is the rule: nothing leaves for a number the
    // numbering plan says cannot receive it.
    if (!canDeliverOtp(phone)) {
      const reason = classifyOtpIneligibility(phone) ?? 'not_a_number'
      recordPhoneRequestEvent(
        'auth.phone_otp_request_validation_failed',
        'phone-login.phone-input',
        'Teléfono no verificable',
        { field: 'phone', reason },
      )
      setIsValid(false)
      setError('')
      setPhoneError(t(otpIneligibilityMessageKey(reason) ?? 'auth.validation.phone_format'))
      void notification(NotificationType.Error)
      return
    }
    setError('')
    setPhoneError('')
    setConsentError(undefined)

    // Registro sin fricción: número y términos, nada más. Nombre y fecha de
    // nacimiento son progresivos (perfil / reserva) — nunca un segundo
    // formulario después del código para las mismas respuestas.
    //
    // Los metadatos ya NO viajan con el envío. La cuenta nace al validar, que es
    // el único momento en que sabemos que del otro lado hay alguien; mandarlos
    // ahora sería describir a un usuario que quizá nunca exista. La pantalla de
    // código los reconstruye de `mode` y del idioma activo.
    if (isSignup && !consent.acceptTerms) {
      setConsentError(t('auth.validation.accept_terms_required'))
      void notification(NotificationType.Error)
      return
    }

    isSendingRef.current = true
    try {
      recordPhoneRequestEvent(
        'auth.phone_otp_request_started',
        'phone-login.submit-button',
        isSignup ? 'Crear cuenta con teléfono' : 'Enviar código',
      )
      await sendOtp({ phone })
      recordPhoneRequestEvent(
        'auth.phone_otp_request_succeeded',
        'phone-login.submit-button',
        'Código enviado',
      )
      void notification(NotificationType.Success)
      setPendingAuthPhone(phone)
      router.push({
        pathname: '/(auth)/phone-verify',
        // Only non-PII flow flags travel in the URL. `phone` is staged in the
        // in-memory auth store — see setPendingAuthPhone above.
        params: { mode },
      })
    } catch (err) {
      recordPhoneRequestEvent(
        'auth.phone_otp_request_failed',
        'phone-login.submit-button',
        'No se pudo enviar código',
      )
      // Capacidad y caídas del proveedor NO son "sin conexión" — esa etiqueta
      // hacía que la gente reintentara en bucle, y cada reintento es un crédito.
      // La tarjeta de recuperación (salida por correo) es mejor destino que un
      // banner sin salida cuando el envío fue RECHAZADO.
      //
      // El contador sale de lo que dijo el servidor (`Retry-After`), no de un
      // número inventado acá: cuando el rechazo es "ya te mandé uno hace un
      // momento", el servidor bloquea 120 segundos y la pantalla tiene que
      // bloquear los mismos 120. Un contador más corto que el bloqueo real es
      // un botón que invita a gastar otro crédito para volver a fallar.
      const reason = classifyPhoneOtpDeliveryError(err)
      if (reason) {
        setDeliveryIssue({ reason })
        setRetrySeconds(
          phoneOtpRetryAfterSeconds(err) ?? (reason === 'provider_rejected' ? 30 : 60),
        )
        setError('')
      } else {
        setDeliveryIssue(null)
        setError(phoneSendErrorMessage(err))
      }
      void notification(NotificationType.Error)
      captureError(err, { screen: 'phone-login', action: 'send-otp' })
    } finally {
      isSendingRef.current = false
    }
  }

  const webCursor = isWeb ? { cursor: 'pointer' as const } : {}

  useEffect(() => {
    if (retrySeconds <= 0) return
    const id = setTimeout(() => setRetrySeconds((s) => Math.max(0, s - 1)), 1000)
    return () => clearTimeout(id)
  }, [retrySeconds])

  if (!env.isPhoneAuthEnabled) {
    return (
      <AuthScaffold background={colors.background}>
        <BackButton />
        <Animated.Text
          entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER)}
          accessibilityRole="header"
          numberOfLines={2}
          adjustsFontSizeToFit
          minimumFontScale={0.88}
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: isWide ? 28 : 30,
            lineHeight: isWide ? 34 : 36,
            marginTop: sp.headerTop,
            color: colors.textPrimary,
          }}
        >
          {t('auth.phone_auth_unavailable_title')}
        </Animated.Text>
        <Animated.Text
          entering={
            reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 2)
          }
          style={{
            fontFamily: FONTS.body,
            fontSize: 15,
            lineHeight: 22,
            marginTop: sp.headlineToSub,
            marginBottom: sp.formToAction,
            color: colors.textSecondary,
          }}
        >
          {t('auth.phone_auth_unavailable_desc')}
        </Animated.Text>
        <AuthGradientButton
          title={isSignup ? t('auth.create_account_button') : t('auth.sign_in_button')}
          onPress={() => router.replace(isSignup ? '/(auth)/register' : '/(auth)/login')}
          testID="phone-login.unavailable-continue-button"
          entering={
            reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 4)
          }
        />
      </AuthScaffold>
    )
  }

  const formContent = (
    <>
      {/* ─── Navigation ─── */}
      <BackButton />

      {/* ─── Header ─── */}
      <Animated.Text
        entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER)}
        accessibilityRole="header"
        numberOfLines={2}
        adjustsFontSizeToFit
        minimumFontScale={0.88}
        style={{
          fontFamily: FONTS.bodyBold,

          fontSize: isWide ? 28 : 30,
          lineHeight: isWide ? 34 : 36,
          marginTop: sp.headerTop,
          color: colors.textPrimary,
        }}
      >
        {isSignup ? t('auth.phone_signup_title') : t('auth.enter_phone_title')}
      </Animated.Text>

      <Animated.Text
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 2)
        }
        style={{
          fontFamily: FONTS.body,

          fontSize: 15,
          lineHeight: 22,
          marginTop: sp.headlineToSub,
          marginBottom: sp.subToForm,
          color: colors.textSecondary,
        }}
      >
        {isSignup ? t('auth.phone_signup_desc') : t('auth.enter_phone_desc')}
      </Animated.Text>

      {/* ─── Phone only — name/DOB progressive after OTP ─── */}
      <Animated.View
        collapsable={false}
        entering={
          reducedMotion ? undefined : FadeInDown.duration(DURATION.normal).delay(STAGGER * 3)
        }
      >
        <PhoneInput
          label={t('auth.phone')}
          value={phone}
          onChange={(v) => {
            setPhone(v)
            setError('')
            setPhoneError('')
          }}
          onValidityChange={handlePhoneValidityChange}
          error={phoneError || undefined}
          helperText={phoneHint ?? undefined}
          testID="phone-login.phone-input"
        />
      </Animated.View>

      {/* ─── Legal consent (signup only) ─── */}
      {isSignup ? (
        <ConsentForm
          values={consent}
          onChange={(next) => {
            setConsent(next)
            if (next.acceptTerms) setConsentError(undefined)
          }}
          errors={consentError ? { acceptTerms: consentError } : undefined}
          testIDPrefix="phone-signup"
        />
      ) : null}

      {/* ─── Action ─── */}
      <View style={{ marginTop: sp.formToAction }}>
        {/* Generic errors only — delivery refusals use the recovery card below. */}
        <AuthErrorBanner error={deliveryIssue ? null : error || null} />

        {deliveryIssue ? (
          <PhoneOtpDeliveryRecovery
            reason={deliveryIssue.reason}
            emailCta={isSignup ? 'create_account_email' : 'sign_in_email'}
            onEmailCta={() => {
              productTelemetryService.recordEvent('form_submit', 'auth.phone_otp_recovery_email', {
                routePath: isSignup ? '/phone-signup' : '/phone-login',
                screenName: isSignup ? 'phone-signup' : 'phone-login',
                elementId: 'phone-login.recovery-email',
                elementType: 'button',
                metadata: { mode },
              })
              // Signup → register (email form + Google). Login → email login
              // (also has Google). Clear CTAs, never a vague "continue to Tago".
              router.replace(isSignup ? '/(auth)/register' : '/(auth)/login')
            }}
            onGoogleCta={() => {
              productTelemetryService.recordEvent('form_submit', 'auth.phone_otp_recovery_google', {
                routePath: isSignup ? '/phone-signup' : '/phone-login',
                screenName: isSignup ? 'phone-signup' : 'phone-login',
                elementId: 'phone-login.recovery-google',
                elementType: 'button',
                metadata: { mode },
              })
              // Full handoff — never fire-and-forget Google (session without
              // completeSignup / route left AuthGate thrashing and looked like
              // a crash or a dead button).
              void (async () => {
                setCompletingSignup(true)
                try {
                  await signInWithGoogle()
                  const { finishSocialSignIn } =
                    await import('../../lib/auth/finish-social-sign-in')
                  const result = await finishSocialSignIn({
                    router,
                    screen: 'phone-login',
                    providerName: 'google',
                    allowCancel: true,
                  })
                  // En el camino feliz la guarda la baja RootLayoutApp cuando
                  // la sesión ya está viva. Acá solo se limpia el fracaso.
                  if (result !== 'ok') setCompletingSignup(false)
                } catch (err) {
                  captureError(err, { screen: 'phone-login', action: 'recovery-google' })
                  const base = errorMessage(err, t('auth.errors.google_failed'))
                  toast.error(base)
                  setCompletingSignup(false)
                }
              })()
            }}
            googleLoading={googlePending}
            onRetry={() => {
              setDeliveryIssue(null)
              setRetrySeconds(0)
              void handleSend()
            }}
            retryLoading={sendOtpPending}
            retryDisabled={retrySeconds > 0}
            retrySeconds={retrySeconds}
            onChangeNumber={() => {
              setDeliveryIssue(null)
              setRetrySeconds(0)
              setError('')
              setPhoneError('')
            }}
            onSupport={() => {
              void openPhoneOtpSupport(t('auth.verify_phone_sms_support_message'), {
                unavailableMessage: t('auth.verify_phone_sms_support_unavailable'),
                screen: 'phone-login',
              })
            }}
            testID="phone-login.delivery-recovery"
          />
        ) : (
          <AuthGradientButton
            title={isSignup ? t('auth.phone_signup_send_code') : t('auth.send_code')}
            onPress={handleSend}
            loading={sendOtpPending}
            // The barrier, made visible. Until libphonenumber says the typed
            // number can actually receive a message, this button is inert and
            // no request exists to be sent. The reason is under the field, from
            // `phoneHint`, so the person is never looking at a dead control
            // with nothing to read.
            disabled={!isValid}
            testID="phone-login.submit-button"
            entering={
              reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 6)
            }
          />
        )}
      </View>

      {/* Mobile only: absorbs whatever the form does not use, so the footer
          closes the screen instead of floating mid-display with ~40% of the
          phone empty underneath it. Collapses to zero the moment the content
          is taller than the viewport (signup mode, keyboard open), so it never
          introduces a scroll. */}
      {isSplit ? null : <View style={{ flex: 1 }} />}

      {/* ─── Footer ─── */}
      <Animated.View
        collapsable={false}
        entering={reducedMotion ? undefined : FadeInUp.duration(DURATION.normal).delay(STAGGER * 7)}
        style={{
          flexDirection: 'row',
          // A single non-wrapping flex line: at 2.0 the sentence plus the link
          // is wider than the screen, and a row that cannot wrap does not
          // clip at one edge — it overflows both, so the render lost the
          // leading "¿" off the left and the final "n" of "Sesión" off the
          // right. Wrapping puts the link on its own line instead.
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          rowGap: SPACING.xs,
          marginTop: sp.altToFooter,
          paddingBottom: isWide ? 0 : SPACING.sm,
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 14,
            flexShrink: 1,
            color: colors.textSecondary,
          }}
        >
          {`${isSignup ? t('auth.already_have_account') : t('auth.dont_have_account')} `}
        </Text>
        <Pressable
          onPress={() =>
            isSignup
              ? router.replace('/(auth)/login')
              : router.replace({
                  pathname: '/(auth)/phone-signup' as never,
                  params: { mode: 'signup' },
                })
          }
          hitSlop={INTERACTION.iconHitSlop}
          testID="phone-login.footer-link"
          // The link was a 20pt-tall text node. hitSlop stays for native, but
          // react-native-web drops it, so the box carries the 44 itself.
          style={{
            ...webCursor,
            minHeight: INTERACTION.minTouchTarget,
            justifyContent: 'center' as const,
            paddingHorizontal: SPACING.xs,
            flexShrink: 1,
          }}
          accessibilityRole="link"
          accessibilityLabel={isSignup ? t('auth.login') : t('auth.register')}
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              // `borderFocused` is a border token, and using it here painted
              // this link purple in light while every other auth footer in the
              // flow (verify-phone, verify-email, reset-password) paints
              // textAccent coral. One accent, both themes.
              color: colors.textAccent,
            }}
          >
            {isSignup ? t('auth.login') : t('auth.register')}
          </Text>
        </Pressable>
      </Animated.View>
    </>
  )

  // Single render path for both breakpoints. AuthScaffold:
  //   - Desktop (≥900px): centers form in 480px column. The BrandPanel
  //     is owned by app/(auth)/_layout.tsx so it persists across route
  //     changes — every other auth screen (login, register, welcome)
  //     uses this same wrapper. phone-login used to render its OWN
  //     BrandPanel here, which stacked a SECOND identical brand panel
  //     next to the layout's panel (Carlos's "duplicated panel" bug).
  //   - Mobile (<900px): wraps the form in KeyboardAwareView so the
  //     keyboard lifts the inputs without clipping.
  return <AuthScaffold background={colors.background}>{formContent}</AuthScaffold>
}
